import { clock, localDate } from "./format";
import type { Channel, HistoryActor, HistoryEvent } from "./types";

/** Area filter: audited entity types with their German noun (singular, title case), in menu order. */
export const AREAS: Record<string, string> = {
  client: "Firma",
  project: "Projekt",
  time_entry: "Buchung",
  invoice: "Rechnung",
  expense: "Ausgabe",
  expense_rule: "Regel",
  expense_share: "Ausgaben-Anteil",
  expense_rule_share: "Regel-Anteil",
  client_domain: "Domain",
  contact: "Kontakt",
  note: "Notiz",
  client_email: "E-Mail-Adresse",
  passkey: "Passkey",
};

const CHANNEL: Record<Channel, string> = {
  web: "in der Web-App",
  api: "über die API",
  mcp: "über MCP",
  cli: "über die Kommandozeile",
  scheduler: "vom Zeitplan",
  import: "beim Import",
  unknown: "ohne Kanal",
};

export const channelPhrase = (c: Channel) => CHANNEL[c] ?? c;

const isMe = (a: { kind: string; name: string }) => a.kind === "user" && a.name === "florian";

/** Who, as the sentence's subject: „Du“ for Florian, else the actor's name. */
export const subject = (a: HistoryEvent["actor"]) => (isMe(a) ? "Du" : a.name);

/** The server words the summary in the third person („hat …“); with „Du“ it becomes „hast …“. */
export function summaryFor(e: HistoryEvent): string {
  return isMe(e.actor) ? e.summary.replace(/^hat /, "hast ") : e.summary;
}

/** Display name in the access card: Florian capitalised, agents by token name. */
export const displayName = (a: Pick<HistoryActor, "kind" | "name">) => (isMe(a) ? "Florian" : a.name);

/** Avatar text: „FB“ for Florian, the first two letters for agents, nothing for system actors. */
export function initials(a: Pick<HistoryActor, "kind" | "name">): string {
  if (isMe(a)) return "FB";
  return a.kind === "agent" ? a.name.slice(0, 2) : "";
}

/** In-app page for an entity, where one exists. */
export function entityHref(e: Pick<HistoryEvent, "entityType" | "entityId">): string | null {
  if (e.entityType === "client") return `/firmen/${e.entityId}`;
  if (e.entityType === "time_entry") return "/buchungen";
  if (e.entityType === "expense") return "/ausgaben";
  return null;
}

/** A before/after value as text; empty values read „–“. */
export function valueText(v: unknown): string {
  if (v === null || v === undefined || v === "") return "–";
  if (typeof v === "boolean") return v ? "ja" : "nein";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export interface HistoryGroup { key: string; events: HistoryEvent[] }
export interface HistoryDay { day: string; groups: HistoryGroup[] }

/**
 * Splits a newest-first feed into local days, and each day into groups of consecutive events sharing a run.
 * A group's key is its run plus its newest event, so it stays stable while older pages are appended.
 */
export function groupHistory(events: HistoryEvent[]): HistoryDay[] {
  const days: HistoryDay[] = [];
  for (const e of events) {
    const day = localDate(e.at);
    let d = days[days.length - 1];
    if (!d || d.day !== day) days.push((d = { day, groups: [] }));
    const g = d.groups[d.groups.length - 1];
    if (g && g.events[0]!.run === e.run) g.events.push(e);
    else d.groups.push({ key: `${e.run}:${e.id}`, events: [e] });
  }
  return days;
}

/** „14:46–14:47“ for a newest-first run, or one time when it started and ended in the same minute. */
export function timeRange(events: HistoryEvent[]): string {
  const from = clock(events[events.length - 1]!.at);
  const to = clock(events[0]!.at);
  return from === to ? to : `${from}–${to}`;
}
